import { Dialog, DialogTitle } from "@mui/material";
import IconQuesionType from "components/IconQuestionType";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
} from "components/LazyTable";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import TruncatedQuestion from "components/TruncatedQuestion";
import { useCallback, useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FieldArrayRenderProps } from "react-final-form-arrays";
import { useIntl } from "react-intl";
import { IQuestion } from "services/KnowfullyAdminClient";
import { QuestionTypeEnum } from "types/common-types";
import { computeCorrectAnswersLetters } from "utils/evaluationQuestionUtils";
import QuestionEditDialog from "./QuestionEditDialog";

type QuestionWithTableId = IQuestion & LazyTableRow;

type EvaluationQuestionsTableProps = {
  fields: FieldArrayRenderProps<IQuestion, HTMLElement>["fields"];
  evaluationOrganizationId: string;
};

const RenderCorrectAnswer = ({ value: question }: { value: IQuestion }) => {
  const isTxtQuestion =
    question.questionType?.value === QuestionTypeEnum.TextQuestion;

  if (isTxtQuestion && question.evaluationQuestionAnswers?.length) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: question.evaluationQuestionAnswers[0].value!,
        }}
      />
    );
  }

  return <>{computeCorrectAnswersLetters(question)}</>;
};

const noop = () => {};

const EvaluationQuestionsTable = ({
  fields,
  evaluationOrganizationId,
}: EvaluationQuestionsTableProps) => {
  const { formatMessage: t } = useIntl();
  const [editingQuestion, setEditingQuestion] = useState<{
    question: IQuestion;
    index: number;
  } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const totalCount = fields.value?.length ?? 0;
  const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const allQuestions: QuestionWithTableId[] = (fields.value ?? []).map(
    (q, i) => ({
      ...q,
      id: q.id!,
      questionNumber: i + 1,
    })
  );

  const paginatedQuestions = allQuestions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const offset = page * rowsPerPage;
      const sourceIndex = offset + result.source.index;
      const destinationIndex = offset + result.destination.index;
      if (sourceIndex !== destinationIndex) {
        fields.move(sourceIndex, destinationIndex);
      }
    },
    [fields, page, rowsPerPage]
  );

  const handleEdit = (index: number) => {
    const question = fields.value[index];
    setEditingQuestion({ question, index });
  };

  const handleDelete = (index: number) => {
    fields.remove(index);
  };

  const handleQuestionUpdated = (updatedQuestion: IQuestion) => {
    if (editingQuestion) {
      fields.update(editingQuestion.index, updatedQuestion);
      setEditingQuestion(null);
    }
  };

  const ActionsCell = ({ value: question }: { value: QuestionWithTableId }) => {
    const index = allQuestions.findIndex((q) => q.id === question.id);
    return (
      <BasicActionsThreeDotsMenu
        onEdit={() => handleEdit(index)}
        onDelete={() => handleDelete(index)}
      />
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "questionNumber",
      type: "text",
      label: "#",
      noSort: true,
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      View: TruncatedQuestion,
      label: t({ id: "questionLabel" }),
      noSort: true,
    },
    {
      accessor: "questionType.name",
      align: "center",
      width: 0,
      type: "node",
      View: IconQuesionType,
      label: t({ id: "questionsTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "required",
      align: "center",
      width: 0,
      type: "boolean",
      label: t({ id: "questionsRequiredLabel" }),
      noSort: true,
    },
    {
      accessor: "correctAnswersLetters",
      align: "left",
      width: 0,
      type: "node",
      View: RenderCorrectAnswer,
      label: t({ id: "correctAnswerLabel" }),
      noSort: true,
    },
    {
      accessor: "actions",
      align: "center",
      width: 50,
      type: "node",
      View: ActionsCell,
      label: "",
      noSort: true,
    },
  ];

  return (
    <>
      <LazyTable
        rows={paginatedQuestions}
        cells={cells}
        rowCount={allQuestions.length}
        dense
        allowDragAndDrop
        onDragAndDropEnd={handleDragEnd}
        noResultsLabel={t({ id: "noQuestionsAdded" })}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order="asc"
        setOrder={noop}
        orderBy="questionNumber"
        setOrderBy={noop}
      />

      <Dialog
        open={!!editingQuestion}
        onClose={() => setEditingQuestion(null)}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
      >
        {editingQuestion && (
          <>
            <DialogTitle>{t({ id: "editQuestionTitle" })}</DialogTitle>
            <QuestionEditDialog
              question={editingQuestion.question}
              evaluationOrganizationId={evaluationOrganizationId}
              onSave={handleQuestionUpdated}
              onCancel={() => setEditingQuestion(null)}
            />
          </>
        )}
      </Dialog>
    </>
  );
};

export default EvaluationQuestionsTable;
